import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Button, Badge, Card, Row, Col, Form, InputGroup, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import { formatImageDate, getImageUrl } from '../utils';
import {
  useDeleteMoleImageMutation,
  useDeleteUserMoleMutation,
  useClassifyMoleImageMutation,
  useConsentAiClassificationMutation,
  useLazyFetchMoleImageStatusQuery,
  useUpdateMoleImageMutation,
} from '../../../store/apis/skinMolesApi';
import { useFetchSubscriptionStatusQuery } from '../../../store/apis/subscriptionApi';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import AiClassificationConsentModal from './AiClassificationConsentModal';
import CreditsRequiredModal from './CreditsRequiredModal';

/**
 * Modal component for displaying mole image carousel with detailed metadata
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether modal is visible
 * @param {Function} props.onHide - Handler to close modal
 * @param {Object} props.mole - Mole data object
 * @param {Array} props.moleImages - Array of mole images
 * @param {string} props.translatedBodyPartName - Translated body part name from parent component
 * @param {Function} props.onMoleDeleted - Callback when mole is deleted
 * @param {Function} props.onRefreshData - Callback to refresh mole images data for polling
 * @param {boolean} props.readOnly - If true, hide edit/delete/classify buttons (for public shared view)
 */
const MoleImageCarouselModal = ({
  show,
  onHide,
  mole,
  moleImages = [],
  translatedBodyPartName,
  onMoleDeleted,
  onRefreshData,
  readOnly = false,
}) => {
  const { t, i18n } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showDeleteImageModal, setShowDeleteImageModal] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showCreditsRequiredModal, setShowCreditsRequiredModal] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  const [isClassificationInProgress, setIsClassificationInProgress] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [updatedImageData, setUpdatedImageData] = useState({});
  const [classificationError, setClassificationError] = useState(null);
  
  // Edit mode states
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({
    approximate_size_mm: '',
    distinctive_features: '',
  });

  // Delete image mutation
  const [deleteImage, { isLoading: isDeletingImage }] = useDeleteMoleImageMutation();

  // Delete mole mutation
  const [deleteMole, { isLoading: isDeletingMole }] = useDeleteUserMoleMutation();

  // Classify image mutation
  const [classifyImage, { isLoading: isClassifying }] = useClassifyMoleImageMutation();

  // Consent mutation
  const [consentAiClassification, { isLoading: isConsenting }] =
    useConsentAiClassificationMutation();

  // Update image mutation
  const [updateMoleImage, { isLoading: isUpdatingImage }] = useUpdateMoleImageMutation();

  // Lazy query for checking single image status during polling
  const [fetchImageStatus] = useLazyFetchMoleImageStatusQuery();

  // Fetch subscription status for credit checking
  const { data: subscriptionStatus } = useFetchSubscriptionStatusQuery();

  // Sort images by creation date (newest first for progression tracking)
  const sortedImages = [...moleImages].sort((a, b) => new Date(b.created) - new Date(a.created));
  const baseCurrentImage = sortedImages[currentImageIndex] || sortedImages[0];

  // Use updated data if available, otherwise use base data - memoized to prevent unnecessary re-renders
  const currentImage = useMemo(() => {
    return baseCurrentImage
      ? {
          ...baseCurrentImage,
          ...(updatedImageData[baseCurrentImage?.uuid] || {}),
        }
      : null;
  }, [baseCurrentImage, updatedImageData]);

  // Reset to first image when modal opens with new mole
  useEffect(() => {
    if (show) {
      setCurrentImageIndex(0);
      setHasConsent(false); // Reset consent when modal opens
      setShowConsentModal(false);
      setIsClassificationInProgress(false); // Reset classification progress
      setUpdatedImageData({}); // Reset updated image data
      setIsEditMode(false); // Reset edit mode
      setClassificationError(null); // Reset classification error
    }
  }, [show, mole?.uuid]);

  // Clear updatedImageData when moleImages change (after refresh)
  // This ensures we use fresh data from props instead of stale local state
  useEffect(() => {
    if (show && moleImages.length > 0) {
      // Only clear if we have fresh data from backend
      // Check if any image has FINISHED status with abcde_description in props
      const hasFreshFinishedData = moleImages.some(
        (img) => img.process_status === 'FINISHED' && img.abcde_description
      );
      
      if (hasFreshFinishedData) {
        // Clear local updates to use fresh data from backend
        setUpdatedImageData({});
        setIsClassificationInProgress(false);
      }
    }
  }, [moleImages, show]);

  // Reset edit form when current image changes
  useEffect(() => {
    if (currentImage) {
      setEditFormData({
        approximate_size_mm: currentImage.approximate_size_mm || '',
        distinctive_features: currentImage.distinctive_features || '',
      });
      setIsEditMode(false); // Exit edit mode when switching images
    }
  }, [currentImage?.uuid, currentImage?.approximate_size_mm, currentImage?.distinctive_features]); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling effect to check classification status for current image
  useEffect(() => {
    // Check if current image has IN_PROGRESS status (using updated data if available)
    const currentImageInProgress = currentImage?.process_status === 'IN_PROGRESS';

    if (currentImageInProgress && show && !pollingInterval && currentImage?.uuid) {
      const interval = setInterval(async () => {
        try {
          // Check status of specific image only
          const result = await fetchImageStatus(currentImage.uuid);
          if (result.data) {

            // Check if status is FINISHED but abcde_description is invalid/missing
            // ABCDE description is critical - if missing or has error, status should be FAILED
            let actualStatus = result.data.process_status;
            const hasInvalidAbcde = 
              result.data.process_status === 'FINISHED' &&
              (hasAbcdeError(result.data.abcde_description) ||
               !result.data.abcde_description ||
               !parseAbcdeDescription(result.data.abcde_description)?.analysis);

            if (hasInvalidAbcde) {
              try {
                await updateMoleImage({
                  uuid: currentImage.uuid,
                  process_status: 'FAILED',
                }).unwrap();
                actualStatus = 'FAILED';
              } catch (updateError) {
                // Silently handle update errors during polling
              }
            }

            // Update only this image's data in local state
            setUpdatedImageData((prev) => ({
              ...prev,
              [currentImage.uuid]: {
                process_status: actualStatus,
                classification_label: result.data.classification_label,
                severity: result.data.severity,
                classification_confidence: result.data.classification_confidence,
                ai_notes: result.data.ai_notes,
                abcde_description: result.data.abcde_description,
              },
            }));

            // If status changed to FINISHED or FAILED, stop polling
            if (actualStatus === 'FINISHED' || actualStatus === 'FAILED') {
              clearInterval(interval);
              setPollingInterval(null);
            }
          }
        } catch (error) {
          // Silently handle polling errors
        }
      }, 5000); // Poll every 5 seconds

      setPollingInterval(interval);
    } else if (!currentImageInProgress && pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }

    // Cleanup on component unmount or modal close
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    };
  }, [currentImage?.uuid, currentImage?.process_status, show, fetchImageStatus, pollingInterval]); // Track current image status

  // Effect to clear local progress state when backend status changes to FINISHED
  useEffect(() => {
    if (currentImage?.process_status === 'FINISHED' && isClassificationInProgress) {
      setIsClassificationInProgress(false);
    }
  }, [currentImage?.process_status, isClassificationInProgress]);

  // Effect to automatically set status to FAILED if FINISHED but abcde_description is null or has error
  // ABCDE description is the most important part - if missing or has error, status should be FAILED
  useEffect(() => {
    if (!currentImage?.uuid || !show) return;

    const shouldBeFailed = 
      currentImage.process_status === 'FINISHED' && 
      (hasAbcdeError(currentImage.abcde_description) || 
       !currentImage.abcde_description ||
       !parseAbcdeDescription(currentImage.abcde_description)?.analysis);

    if (shouldBeFailed) {
      // Update status to FAILED on backend
      updateMoleImage({
        uuid: currentImage.uuid,
        process_status: 'FAILED',
      })
        .unwrap()
        .then(() => {
          // Update local state
          setUpdatedImageData((prev) => ({
            ...prev,
            [currentImage.uuid]: {
              ...prev[currentImage.uuid],
              process_status: 'FAILED',
            },
          }));
          // Refresh parent data
          if (onRefreshData) {
            onRefreshData().catch(() => {});
          }
        })
        .catch(() => {
          // Silently handle update errors
        });
    }
  }, [currentImage?.uuid, currentImage?.process_status, currentImage?.abcde_description, show, updateMoleImage, onRefreshData]);

  // Navigation functions
  const goToPrevious = useCallback(() => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : sortedImages.length - 1));
  }, [sortedImages.length]);

  const goToNext = useCallback(() => {
    setCurrentImageIndex((prev) => (prev < sortedImages.length - 1 ? prev + 1 : 0));
  }, [sortedImages.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!show) return;
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'Escape') onHide();
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [show, goToNext, goToPrevious, onHide]);

  // Delete image handlers
  const handleDeleteImageClick = () => {
    setShowDeleteImageModal(true);
  };

  const handleDeleteImageConfirm = async () => {
    try {
      await deleteImage({ uuid: currentImage.uuid }).unwrap();
      setShowDeleteImageModal(false);

      // If this was the last image, also delete the mole and close the modal
      if (sortedImages.length === 1) {
        try {
          // Delete the mole since it has no images left
          await deleteMole({ uuid: mole.uuid }).unwrap();

          // Notify parent component that mole was deleted
          if (onMoleDeleted) {
            onMoleDeleted();
          }
        } catch (moleDeleteError) {
          // Silently handle mole deletion errors
        }

        onHide();
        return;
      }

      // Navigate to next image or previous if we deleted the last one
      const newIndex =
        currentImageIndex >= sortedImages.length - 1
          ? Math.max(0, currentImageIndex - 1)
          : currentImageIndex;
      setCurrentImageIndex(newIndex);

      // Notify parent component to refresh data after image deletion
      if (onMoleDeleted) {
        onMoleDeleted();
      }
    } catch (error) {
      // Silently handle image deletion errors
    }
  };

  const handleDeleteImageCancel = () => {
    setShowDeleteImageModal(false);
  };

  // Show consent modal handler
  const handleClassifyImage = () => {
    if (!currentImage?.uuid) return;

    // If user already has consent, proceed directly with classification
    if (hasConsent) {
      handleActualClassification();
    } else {
      // Show consent modal first
      setShowConsentModal(true);
    }
  };

  // Handle consent given
  const handleConsentGiven = async (imageUuid) => {
    try {
      await consentAiClassification({ image_uuid: imageUuid }).unwrap();

      setHasConsent(true);
      setShowConsentModal(false);

      // Auto-proceed with classification after consent
      handleActualClassification();
    } catch (error) {
      // Keep modal open on error so user can retry
    }
  };

  // Actual classification handler
  const handleActualClassification = async () => {
    if (!currentImage?.uuid) return;

    // Check credits using service_availability for SKIN_CLASSIFY if available
    const skinClassifyService = subscriptionStatus?.service_availability?.SKIN_CLASSIFY;
    const canUse = skinClassifyService
      ? skinClassifyService.can_use
      : (subscriptionStatus?.total_available ?? (subscriptionStatus?.creds + subscriptionStatus?.extra_credits - subscriptionStatus?.counts)) > 0;

    if (!canUse) {
      setShowCreditsRequiredModal(true);
      return;
    }

    try {
      // If this is a retry after a failed analysis, try to reset the status and clear error data first
      // This ensures ABCDE analysis can be rerun if it failed for any reason
      if (hasFailedAnalysis(currentImage)) {
        try {
          // Always reset process_status to NOT_STARTED and clear abcde_description
          // This ensures we can rerun ABCDE analysis regardless of why it failed
          const updateData = {
            uuid: currentImage.uuid,
            process_status: 'NOT_STARTED',
            abcde_description: null, // Always clear abcde_description to allow fresh analysis
          };
          
          await updateMoleImage(updateData).unwrap();
          
          // Update local state to reflect the reset
          setUpdatedImageData((prev) => ({
            ...prev,
            [currentImage.uuid]: {
              ...prev[currentImage.uuid],
              process_status: 'NOT_STARTED',
              abcde_description: null,
            },
          }));
          
          // Refresh parent data to get updated status from backend
          if (onRefreshData) {
            await onRefreshData();
          }
        } catch (clearError) {
          // Continue with classification even if reset fails
        }
      }

      // Set local state to indicate classification is in progress
      setIsClassificationInProgress(true);
      setClassificationError(null); // Clear any previous errors

      // Map language code to language_id: 'pl' = 1, 'en' = 2
      // Use same logic as SkinMolesComponent for consistency
      const currentLang = i18n.language;
      const isPolish = String(currentLang || '').toLowerCase().startsWith('pl');
      const languageId = isPolish ? 1 : 2; // Polish = 1, English = 2

      await classifyImage({ 
        image_uuid: currentImage.uuid,
        language_id: languageId,
      }).unwrap();

      // Immediately set status to IN_PROGRESS in local state to start polling
      setUpdatedImageData((prev) => ({
        ...prev,
        [currentImage.uuid]: {
          ...prev[currentImage.uuid],
          process_status: 'IN_PROGRESS',
          abcde_description: null, // Clear any previous error data
        },
      }));

      // Also refresh the parent data, but don't wait for it
      if (onRefreshData) {
        onRefreshData().catch(() => {});
      }
    } catch (error) {
      // Check if the error is due to insufficient credits (402 Payment Required)
      if (error.status === 402) {
        setShowCreditsRequiredModal(true);
      } else if (error.status === 400) {
        // 400 Bad Request - might be because analysis is already in progress or other validation error
        const errorMessage = error.data?.detail || error.data?.message || t('skinMoles.imageCarousel.retryFailed') || 'Failed to retry analysis. Please try again later.';
        // Store error message to display to user
        setClassificationError(errorMessage);
      } else {
        // Other errors
        const errorMessage = error.data?.detail || error.data?.message || t('skinMoles.imageCarousel.classificationError') || 'An error occurred during classification.';
        setClassificationError(errorMessage);
      }

      // Reset progress state on error so user can retry
      setIsClassificationInProgress(false);
    }
  };

  // Edit mode handlers
  const handleEditClick = () => {
    if (readOnly) return; // Don't allow editing in read-only mode
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    // Reset form data to current image values
    setEditFormData({
      approximate_size_mm: currentImage?.approximate_size_mm || '',
      distinctive_features: currentImage?.distinctive_features || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!currentImage?.uuid) return;

    try {
      // Validate data
      const sizeValue = editFormData.approximate_size_mm;
      if (sizeValue && (isNaN(sizeValue) || parseFloat(sizeValue) < 0)) {
        alert(t('skinMoles.addImageToMole.errors.sizePositive'));
        return;
      }

      // Prepare update data
      const updateData = {
        uuid: currentImage.uuid,
        approximate_size_mm: sizeValue ? parseFloat(sizeValue) : null,
        distinctive_features: editFormData.distinctive_features || null,
      };

      await updateMoleImage(updateData).unwrap();

      // Update local state immediately
      setUpdatedImageData((prev) => ({
        ...prev,
        [currentImage.uuid]: {
          ...prev[currentImage.uuid],
          approximate_size_mm: updateData.approximate_size_mm,
          distinctive_features: updateData.distinctive_features,
        },
      }));

      // Exit edit mode
      setIsEditMode(false);

      // Also refresh the parent data
      if (onRefreshData) {
        onRefreshData().catch(() => {});
      }
    } catch (error) {
      alert(t('skinMoles.addImageToMole.errors.addImageFailed'));
    }
  };

  const handleFormChange = (field, value) => {
    // For size field, only allow positive integers
    if (field === 'approximate_size_mm') {
      // Remove any non-digit characters except for empty string
      const cleanValue = value.replace(/[^0-9]/g, '');
      // Only update if it's a valid positive integer or empty
      if (cleanValue === '' || (parseInt(cleanValue) > 0 && parseInt(cleanValue) <= 100)) {
        setEditFormData((prev) => ({
          ...prev,
          [field]: cleanValue,
        }));
      }
    } else {
      setEditFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  // Helper function to check if abcde_description contains an error
  const hasAbcdeError = (abcdeDescription) => {
    if (!abcdeDescription) return false;
    
    try {
      let parsed;
      if (typeof abcdeDescription === 'object') {
        parsed = abcdeDescription;
      } else if (typeof abcdeDescription === 'string') {
        parsed = JSON.parse(abcdeDescription);
      } else {
        return false;
      }
      
      // Check if it contains error fields indicating a failed generation
      return parsed.error || parsed.status === 'failed' || parsed.status === 'error';
    } catch (error) {
      // If parsing fails, it might be an error string
      if (typeof abcdeDescription === 'string') {
        return abcdeDescription.includes('error') || abcdeDescription.includes('failed');
      }
      return false;
    }
  };

  // Helper function to parse ABCDE description JSON
  const parseAbcdeDescription = (abcdeDescription) => {
    if (!abcdeDescription) return null;
    
    // If it contains an error, don't parse it as valid analysis
    if (hasAbcdeError(abcdeDescription)) {
      return null;
    }
    
    try {
      // If it's already an object, return it
      if (typeof abcdeDescription === 'object') {
        return abcdeDescription;
      }
      
      // If it's a string, try to parse it
      if (typeof abcdeDescription === 'string') {
        return JSON.parse(abcdeDescription);
      }
      
      return null;
    } catch (error) {
      return null;
    }
  };

  /**
   * Helper function to check if image has failed analysis
   * ABCDE description is the most important part of the analysis, so we treat any case
   * where it's missing, null, or contains errors as a failed analysis that can be rerun.
   * 
   * @param {Object} image - The mole image object
   * @returns {boolean} - True if analysis failed and should be rerunnable
   */
  const hasFailedAnalysis = (image) => {
    if (!image) return false;
    
    // Check if status is FAILED
    if (image.process_status === 'FAILED') {
      return true;
    }
    
    // Check if status is FINISHED but contains error in abcde_description
    // ABCDE description is critical - if it has errors, analysis failed
    if (image.process_status === 'FINISHED' && hasAbcdeError(image.abcde_description)) {
      return true;
    }
    
    // Check if status is FINISHED but abcde_description is null or empty
    // ABCDE description is required - if missing, analysis is incomplete/failed
    if (image.process_status === 'FINISHED' && !image.abcde_description) {
      return true;
    }
    
    // Check if status is FINISHED but abcde_description doesn't contain valid analysis data
    // ABCDE description must have valid analysis object - if not, analysis failed
    if (image.process_status === 'FINISHED' && !parseAbcdeDescription(image.abcde_description)?.analysis) {
      return true;
    }
    
    return false;
  };

  // Helper function to get risk level color
  const getRiskLevelColor = (riskLevel) => {
    if (!riskLevel) return 'secondary';

    const level = String(riskLevel).toUpperCase();

    if (level === 'LOW' || level === 'VERY_LOW') return 'success';
    if (level === 'MEDIUM' || level === 'MODERATE') return 'warning';
    if (level === 'HIGH' || level === 'VERY_HIGH') return 'danger';

    return 'secondary';
  };

  // Helper function to get class color
  const getClassColor = (className) => {
    if (!className) return 'secondary';
    
    const classUpper = String(className).toUpperCase();
    
    // Dangerous classes
    if (classUpper === 'MELANOMA' || classUpper === 'BASAL_CELL_CARCINOMA') {
      return 'danger';
    }
    
    // Suspicious classes
    if (classUpper === 'ACTINIC_KERATOSES' || classUpper === 'VASCULAR_LESIONS') {
      return 'warning';
    }
    
    // Benign classes
    if (classUpper === 'MELANOCYTIC_NEVUS' || classUpper === 'BENIGN_KERATOSIS_LIKE' || classUpper === 'DERMATOFIBROMA') {
      return 'success';
    }
    
    // Unknown
    return 'secondary';
  };

  // Helper function to get mole badge color
  const getMoleBadgeColor = (badge) => {
    if (!badge) return 'secondary';
    
    const badgeUpper = String(badge).toUpperCase();
    
    if (badgeUpper === 'CRITICAL') return 'danger';
    if (badgeUpper === 'HIGH') return 'danger';
    if (badgeUpper === 'MODERATE') return 'warning';
    if (badgeUpper === 'LOW') return 'success';
    if (badgeUpper === 'UNKNOWN') return 'info';
    if (badgeUpper === 'NOT_CLASSIFIED') return 'secondary';
    
    return 'secondary';
  };

  // Get mole badge and AI description from mole object
  const moleBadge = mole?.mole_badge || mole?.user_mole?.mole_badge;
  const aiDescription = mole?.description_ai || mole?.user_mole?.description_ai;

  if (!mole || sortedImages.length === 0) return null;

  const imageDate = formatImageDate(currentImage?.created, t);
  const isReferenceImage = currentImage?.uuid === mole.reference_image;

  return (
    <Modal show={show} onHide={onHide} size='lg' centered>
      <Modal.Header closeButton className='border-0 pb-0'>
        <Modal.Title className='d-flex align-items-center justify-content-between w-100'>
          <div className='d-flex align-items-center gap-2 flex-wrap'>
            <span className='me-2'>{mole.name || t('skinMoles.imageCarousel.unnamedMole')}</span>
            <Badge bg='secondary' className='fs-6'>
              {mole.mole_code}
            </Badge>
            {moleBadge && (
              <div className='d-flex align-items-center gap-1'>
                <small className='text-muted' style={{ fontSize: '0.75rem' }}>
                  {t('skinMoles.imageCarousel.riskLevel')}:
                </small>
                <Badge bg={getMoleBadgeColor(moleBadge)} className='fs-6'>
                  {t(`skinMoles.moleBadge.${moleBadge}`) || moleBadge}
                </Badge>
              </div>
            )}
          </div>

          {/* Delete image button - hidden in read-only mode */}
          {!readOnly && sortedImages.length > 0 && (
            <Button
              variant='outline-danger'
              size='sm'
              onClick={handleDeleteImageClick}
              className='d-flex align-items-center gap-1 px-2 py-1'
              style={{ fontSize: '0.75rem' }}
              title={t('skinMoles.imageCarousel.deleteImageTitle')
                .replace('{{current}}', currentImageIndex + 1)
                .replace('{{total}}', sortedImages.length)}
              disabled={isDeletingImage || isDeletingMole}
            >
              {isDeletingImage || isDeletingMole
                ? t('skinMoles.deleteConfirmation.deleting')
                : t('skinMoles.imageCarousel.deleteImage')}
            </Button>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className='px-4 py-3'>
        {/* Image display area */}
        <div className='position-relative mb-4'>
          <div
            className='d-flex justify-content-center align-items-center position-relative'
            style={{ minHeight: '400px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}
          >
            <img
              src={getImageUrl(currentImage, true)}
              alt={`${mole.name || mole.description} - ${imageDate.date}`}
              style={{
                maxWidth: '100%',
                maxHeight: '400px',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              }}
              onError={(e) => {
                e.target.src = '/api/placeholder/400/400';
              }}
            />

            {/* Navigation arrows */}
            {sortedImages.length > 1 && (
              <>
                <Button
                  variant='light'
                  className='position-absolute start-0 top-50 translate-middle-y ms-3'
                  style={{
                    borderRadius: '50%',
                    width: '50px',
                    height: '50px',
                    fontSize: '20px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  }}
                  onClick={goToPrevious}
                >
                  ‹
                </Button>
                <Button
                  variant='light'
                  className='position-absolute end-0 top-50 translate-middle-y me-3'
                  style={{
                    borderRadius: '50%',
                    width: '50px',
                    height: '50px',
                    fontSize: '20px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  }}
                  onClick={goToNext}
                >
                  ›
                </Button>
              </>
            )}

            {/* Image badges */}
            <div className='position-absolute top-0 start-0 m-3'>
              {sortedImages.length > 1 && (
                <Badge bg='primary' className='me-2'>
                  {currentImageIndex + 1} / {sortedImages.length}
                </Badge>
              )}
              {isReferenceImage && <Badge bg='success'>📌 Reference</Badge>}
            </div>
          </div>
        </div>

        {/* Image metadata */}
        <Row>
          <Col md={6}>
            <Card className='h-100'>
              <Card.Header className='bg-light'>
                <h6 className='mb-0'>{t('skinMoles.imageCarousel.imageInformation')}</h6>
              </Card.Header>
              <Card.Body>
                <div className='mb-2'>
                  <strong>{t('skinMoles.imageCarousel.date')}</strong> {imageDate.date}
                </div>
                <div className='mb-2'>
                  <strong>{t('skinMoles.imageCarousel.time')}</strong> {imageDate.time}
                </div>
                <div className='mb-2'>
                  <strong>{t('skinMoles.imageCarousel.taken')}</strong>{' '}
                  <span className='text-primary'>{imageDate.relative}</span>
                </div>
                {translatedBodyPartName && (
                  <div className='mb-2'>
                    <strong>{t('skinMoles.imageCarousel.bodyPart')}</strong>{' '}
                    {translatedBodyPartName}
                  </div>
                )}
                <div className='mb-2'>
                  <strong>{t('skinMoles.imageCarousel.moleName')}</strong>{' '}
                  {mole?.name || t('skinMoles.imageCarousel.notAvailable')}
                </div>
                <div className='mb-2'>
                  <strong>{t('skinMoles.imageCarousel.moleDescription')}</strong>{' '}
                  {mole?.description || t('skinMoles.imageCarousel.notAvailable')}
                </div>
                {/* AI Description */}
                <div className='mb-3'>
                  {aiDescription ? (
                    <>
                      <div className='d-flex align-items-center gap-2 mb-2'>
                        <i className='bi bi-robot' style={{ fontSize: '1.1rem', color: '#0d6efd' }}></i>
                        <strong className='text-primary'>{t('skinMoles.imageCarousel.aiDescription')}</strong>
                      </div>
                      <div 
                        className='p-3 rounded border' 
                        style={{ 
                          fontSize: '0.875rem',
                          backgroundColor: '#e7f3ff',
                          borderColor: '#0d6efd !important',
                          borderWidth: '2px',
                          boxShadow: '0 2px 4px rgba(13, 110, 253, 0.1)'
                        }}
                      >
                        <Markdown>{aiDescription}</Markdown>
                      </div>
                    </>
                  ) : (
                    <>
                      <strong>{t('skinMoles.imageCarousel.aiDescription')}</strong>
                      <span className='ms-2 text-muted'>
                        {t('skinMoles.imageCarousel.noAiDescription')}
                      </span>
                    </>
                  )}
                </div>
                {/* Editable Size Field */}
                <div className='mb-3'>
                  <strong>{t('skinMoles.imageCarousel.size')}</strong>
                  {isEditMode ? (
                    <div className='mt-1'>
                      <InputGroup size='sm' style={{ maxWidth: '200px' }}>
                        <Form.Control
                          type='number'
                          value={editFormData.approximate_size_mm}
                          onChange={(e) => handleFormChange('approximate_size_mm', e.target.value)}
                          placeholder={t('skinMoles.addImageToMole.approximateSizePlaceholder')}
                          min='1'
                          max='100'
                          step='1'
                          disabled={isUpdatingImage}
                        />
                        <InputGroup.Text>mm</InputGroup.Text>
                      </InputGroup>
                    </div>
                  ) : (
                    <span className='ms-2'>
                      {currentImage?.approximate_size_mm || mole?.approximate_size_mm
                        ? `${currentImage?.approximate_size_mm || mole?.approximate_size_mm}mm`
                        : t('skinMoles.imageCarousel.notAvailable')}
                    </span>
                  )}
                </div>
                
                {/* Editable Features Field */}
                <div className='mb-3'>
                  <strong>{t('skinMoles.imageCarousel.features')}</strong>
                  {isEditMode ? (
                    <div className='mt-1'>
                      <Form.Control
                        as='textarea'
                        rows={3}
                        size='sm'
                        value={editFormData.distinctive_features}
                        onChange={(e) => handleFormChange('distinctive_features', e.target.value)}
                        placeholder={t('skinMoles.addImageToMole.distinctiveFeaturesPlaceholder')}
                        disabled={isUpdatingImage}
                        style={{ resize: 'vertical' }}
                      />
                    </div>
                  ) : (
                    <span className='ms-2'>
                      {currentImage?.distinctive_features ||
                        mole?.distinctive_features ||
                        t('skinMoles.imageCarousel.notAvailable')}
                    </span>
                  )}
                </div>
                
                {/* Edit Controls - hidden in read-only mode */}
                {!readOnly && (
                  <div className='d-flex gap-2 mt-3'>
                    {isEditMode ? (
                      <>
                        <Button
                          variant='success'
                          size='sm'
                          onClick={handleSaveEdit}
                          disabled={isUpdatingImage}
                          className='d-flex align-items-center justify-content-center gap-1 px-3 py-2'
                          style={{ fontSize: '0.8rem' }}
                        >
                          {isUpdatingImage ? (
                            <>
                              <div
                                className='spinner-border spinner-border-sm'
                                role='status'
                                aria-hidden='true'
                                style={{ width: '12px', height: '12px' }}
                              ></div>
                              {t('forms.image.save')}...
                            </>
                          ) : (
                            <>💾 {t('forms.image.save')}</>
                          )}
                        </Button>
                        <Button
                          variant='outline-secondary'
                          size='sm'
                          onClick={handleCancelEdit}
                          disabled={isUpdatingImage}
                          className='d-flex align-items-center justify-content-center gap-1 px-3 py-2'
                          style={{ fontSize: '0.8rem' }}
                        >
                          ❌ {t('forms.image.cancel')}
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant='outline-success'
                        size='sm'
                        onClick={handleEditClick}
                        className='d-flex align-items-center justify-content-center gap-1 px-3 py-2'
                        style={{ fontSize: '0.8rem' }}
                        title={t('forms.image.edit')}
                      >
                        ✏️ {t('forms.image.edit')}
                      </Button>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className='h-100'>
              <Card.Header className='bg-light'>
                <h6 className='mb-0'>{t('skinMoles.imageCarousel.aiAnalysis')}</h6>
              </Card.Header>
              <Card.Body>
                {/* Show classification error if any */}
                {classificationError && (
                  <Alert variant='danger' dismissible onClose={() => setClassificationError(null)} className='mb-3'>
                    <strong>❌ {t('skinMoles.imageCarousel.classificationError') || 'Classification Error'}</strong>
                    <br />
                    <small>{classificationError}</small>
                  </Alert>
                )}


                {/* ABCDE Analysis Display - FINISHED status with valid ABCDE data */}
                {/* Only show if we have valid ABCDE analysis data and status is FINISHED */}
                {(() => {
                  const abcdeData = parseAbcdeDescription(currentImage?.abcde_description);
                  const hasAbcdeData = abcdeData && abcdeData.analysis;
                  
                  // Only display ABCDE analysis if:
                  // 1. Status is FINISHED
                  // 2. We have valid ABCDE data (not null, not error, has analysis object)
                  // 3. Analysis has not failed (hasFailedAnalysis returns false)
                  if (hasAbcdeData && 
                      currentImage?.process_status === 'FINISHED' && 
                      !hasFailedAnalysis(currentImage)) {
                    const analysis = abcdeData.analysis;
                    
                    return (
                      <>
                        {/* ABCDE Method Description */}
                        <div className='mb-3 p-2 bg-light rounded'>
                          <small className='text-muted'>
                            <strong>{t('skinMoles.imageCarousel.abcdeAnalysis')}</strong>
                            <br />
                            {t('skinMoles.imageCarousel.abcdeDescription')}
                          </small>
                        </div>

                        {/* ABCDE Criteria */}
                        {analysis.A && (
                          <div className='mb-2'>
                            <strong>{t('skinMoles.imageCarousel.abcdeA')}</strong>
                            <div className='mt-1 ps-3'>
                              <small>{analysis.A}</small>
                            </div>
                          </div>
                        )}

                        {analysis.B && (
                          <div className='mb-2'>
                            <strong>{t('skinMoles.imageCarousel.abcdeB')}</strong>
                            <div className='mt-1 ps-3'>
                              <small>{analysis.B}</small>
                            </div>
                          </div>
                        )}

                        {analysis.C && (
                          <div className='mb-2'>
                            <strong>{t('skinMoles.imageCarousel.abcdeC')}</strong>
                            <div className='mt-1 ps-3'>
                              <small>{analysis.C}</small>
                            </div>
                          </div>
                        )}

                        {analysis.D && (
                          <div className='mb-2'>
                            <strong>{t('skinMoles.imageCarousel.abcdeD')}</strong>
                            <div className='mt-1 ps-3'>
                              <small>{analysis.D}</small>
                            </div>
                          </div>
                        )}

                        {analysis.E && (
                          <div className='mb-2'>
                            <strong>{t('skinMoles.imageCarousel.abcdeE')}</strong>
                            <div className='mt-1 ps-3'>
                              <small>{analysis.E}</small>
                            </div>
                          </div>
                        )}

                        {/* Risk Level */}
                        {analysis.Risk_Level && (
                          <div className='mb-2 mt-3'>
                            <strong>{t('skinMoles.imageCarousel.abcdeRiskLevel')}</strong>{' '}
                            <Badge bg={getRiskLevelColor(analysis.Risk_Level)} className='ms-2'>
                              {t(`skinMoles.abcdeRiskLevels.${analysis.Risk_Level}`) || analysis.Risk_Level}
                            </Badge>
                          </div>
                        )}

                        {/* Probable Class */}
                        {analysis.Class && (
                          <div className='mb-2'>
                            <strong>{t('skinMoles.imageCarousel.abcdeClass')}</strong>{' '}
                            <Badge bg={getClassColor(analysis.Class)} className='ms-2'>
                              {t(`skinMoles.classificationLabels.${analysis.Class}`) || analysis.Class}
                            </Badge>
                          </div>
                        )}

                        {/* Alert */}
                        {analysis.Alert && (
                          <div className='mb-2'>
                            <div className='alert alert-warning py-2 mb-0'>
                              <strong>{t('skinMoles.imageCarousel.abcdeAlert')}</strong>
                              <br />
                              <small>
                                {typeof analysis.Alert === 'boolean' 
                                  ? t('skinMoles.imageCarousel.abcdeAlertMessage')
                                  : analysis.Alert}
                              </small>
                            </div>
                          </div>
                        )}

                        {/* Summary */}
                        {analysis.Summary && (
                          <div className='mb-2 mt-3'>
                            <strong>{t('skinMoles.imageCarousel.abcdeSummary')}</strong>
                            <div className='mt-1 p-2 bg-light rounded'>
                              <small>{analysis.Summary}</small>
                            </div>
                          </div>
                        )}

                        {/* Success message */}
                        <div className='alert alert-success mt-3'>
                          <small>
                            ✅ <strong>{t('skinMoles.imageCarousel.analysisCompleted')}</strong>
                            <br />
                            <em>{t('skinMoles.imageCarousel.analysisCompletedHint')}</em>
                          </small>
                        </div>
                      </>
                    );
                  }
                  
                  return null;
                })()}

                {/*
                  ==================== LEGACY VIT CLASSIFICATION DISPLAY ====================
                  The old VIT classifier display code (classification_label, severity, 
                  classification_confidence, ai_notes) has been REMOVED and replaced by ABCDE analysis.
                  
                  If you need to restore the VIT classification display, check the git history
                  of this file. The code was removed because JSX comments cannot safely contain
                  regex patterns with comment-like sequences.
                  
                  To restore: git log --all --full-history -- MoleImageCarouselModal.js
                  ============================================================================
                */}

                {/* Show processing status for images being analyzed */}
                {currentImage?.process_status === 'IN_PROGRESS' && (
                  <>
                    <div className='mb-3'>
                      <div className='d-flex align-items-center gap-2'>
                        <div
                          className='spinner-border spinner-border-sm text-primary'
                          role='status'
                          aria-hidden='true'
                        ></div>
                        <strong>{t('skinMoles.imageCarousel.analysisInProgress')}</strong>
                      </div>
                    </div>
                    <div className='alert alert-info'>
                      <small>
                        <em>{t('skinMoles.imageCarousel.analysisInProgressHint')}</em>
                      </small>
                    </div>
                  </>
                )}

                {/* Show local analysis progress when we just started the process */}
                {isClassificationInProgress &&
                  currentImage?.process_status !== 'IN_PROGRESS' &&
                  currentImage?.process_status !== 'FINISHED' && (
                    <>
                      <div className='mb-3'>
                        <div className='d-flex align-items-center gap-2'>
                          <div
                            className='spinner-border spinner-border-sm text-info'
                            role='status'
                            aria-hidden='true'
                          ></div>
                          <strong>{t('skinMoles.imageCarousel.analysisStarted')}</strong>
                        </div>
                      </div>
                      <div className='alert alert-info'>
                        <small>
                          <em>{t('skinMoles.imageCarousel.analysisStartedHint')}</em>
                        </small>
                      </div>
                    </>
                  )}

                {/* Show FAILED status for analysis errors (including FINISHED with error in abcde_description) */}
                {hasFailedAnalysis(currentImage) && (
                  <>
                    <div className='alert alert-warning'>
                      <strong>⚠️ {t('skinMoles.imageCarousel.analysisFailed')}</strong>
                      <br />
                      <small>
                        <em>{t('skinMoles.imageCarousel.analysisFailedHint')}</em>
                      </small>
                    </div>
                    {!readOnly && (
                      <div className='mt-3 d-grid'>
                        <Button
                          variant='primary'
                          onClick={handleClassifyImage}
                          disabled={isClassifying || isConsenting || !currentImage?.uuid}
                          className='d-flex align-items-center justify-content-center gap-2'
                        >
                          {isClassifying ? (
                            <>
                              <div
                                className='spinner-border spinner-border-sm'
                                role='status'
                                aria-hidden='true'
                              ></div>
                              {t('skinMoles.imageCarousel.classifyingImage')}
                            </>
                          ) : (
                            t('skinMoles.imageCarousel.retryAnalysis')
                          )}
                        </Button>
                      </div>
                    )}
                  </>
                )}

                {/* Show FINISHED status but no valid ABCDE data (and not a failed analysis) */}
                {currentImage?.process_status === 'FINISHED' &&
                  !hasFailedAnalysis(currentImage) &&
                  !parseAbcdeDescription(currentImage?.abcde_description)?.analysis && (
                    <>
                      <div className='alert alert-warning'>
                        <strong>⚠️ {t('skinMoles.imageCarousel.analysisIncomplete')}</strong>
                        <br />
                        <small>
                          <em>{t('skinMoles.imageCarousel.analysisIncompleteHint')}</em>
                        </small>
                      </div>
                      {!readOnly && (
                        <div className='mt-3 d-grid'>
                          <Button
                            variant='primary'
                            onClick={handleClassifyImage}
                            disabled={isClassifying || isConsenting || !currentImage?.uuid}
                            className='d-flex align-items-center justify-content-center gap-2'
                          >
                            {isClassifying ? (
                              <>
                                <div
                                  className='spinner-border spinner-border-sm'
                                  role='status'
                                  aria-hidden='true'
                                ></div>
                                {t('skinMoles.imageCarousel.classifyingImage')}
                              </>
                            ) : (
                              t('skinMoles.imageCarousel.retryAnalysis')
                            )}
                          </Button>
                        </div>
                      )}
                    </>
                  )}

                {/* Show NOT_ANALYZED section only for truly unanalyzed images that are not being processed */}
                {!parseAbcdeDescription(currentImage?.abcde_description)?.analysis &&
                  (!currentImage?.process_status ||
                    currentImage?.process_status === 'NOT_STARTED') &&
                  !isClassificationInProgress && (
                    <>
                      <div className='mb-3 p-3 bg-light rounded text-center'>
                        <div className='mb-2'>
                          <i className='bi bi-robot' style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                        </div>
                        <p className='mb-0'>
                          <strong>{t('skinMoles.imageCarousel.noAbcdeAnalysis')}</strong>
                        </p>
                      </div>
                      {!readOnly && (
                        <>
                          <div className='alert alert-info'>
                            <small>
                              <em>{t('skinMoles.imageCarousel.classificationHint')}</em>
                            </small>
                          </div>
                          <div className='mt-3 d-grid'>
                            <Button
                              variant='primary'
                              onClick={handleClassifyImage}
                              disabled={isClassifying || isConsenting || !currentImage?.uuid}
                              className='d-flex align-items-center justify-content-center gap-2'
                            >
                              {isClassifying ? (
                                <>
                                  <div
                                    className='spinner-border spinner-border-sm'
                                    role='status'
                                    aria-hidden='true'
                                  ></div>
                                  {t('skinMoles.imageCarousel.classifyingImage')}
                                </>
                              ) : hasConsent ? (
                                t('skinMoles.imageCarousel.classifyImageButton')
                              ) : (
                                t('skinMoles.imageCarousel.requestConsentButton')
                              )}
                            </Button>
                          </div>
                        </>
                      )}
                    </>
                  )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Timeline indicator for multiple images */}
        {sortedImages.length > 1 && (
          <div className='mt-4'>
            <h6>{t('skinMoles.imageCarousel.imageTimeline')}</h6>
            <div className='d-flex flex-wrap gap-2'>
              {sortedImages.map((img, index) => {
                const imgDate = formatImageDate(img.created, t);
                return (
                  <Button
                    key={img.uuid}
                    variant={index === currentImageIndex ? 'primary' : 'outline-secondary'}
                    size='sm'
                    onClick={() => setCurrentImageIndex(index)}
                    className='d-flex flex-column align-items-center'
                    style={{ minWidth: '80px' }}
                  >
                    <div style={{ fontSize: '0.7rem' }}>{imgDate.date}</div>
                    <div style={{ fontSize: '0.6rem', opacity: 0.8 }}>{imgDate.relative}</div>
                  </Button>
                );
              })}
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className='border-0 pt-0'>
        <div className='d-flex justify-content-between w-100 align-items-center'>
          <div className='text-muted'>
            <small>{t('skinMoles.imageCarousel.navigationHelp')}</small>
          </div>
          <Button variant='secondary' onClick={onHide}>
            {t('skinMoles.imageCarousel.close')}
          </Button>
        </div>
      </Modal.Footer>

      {/* Delete Image Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteImageModal}
        onHide={handleDeleteImageCancel}
        onConfirm={handleDeleteImageConfirm}
        title={t('skinMoles.imageCarousel.deleteImageTitle')}
        message={
          <>
            {t('skinMoles.imageCarousel.deleteImageMessage').replace(
              '{{moleCode}}',
              mole.mole_code,
            )}
            <br />
            <br />
            <div className='mt-2'>
              <strong>{t('skinMoles.imageCarousel.imageDetails')}</strong>
              <ul className='mt-1 mb-0'>
                <li>
                  {t('skinMoles.imageCarousel.takenInfo')
                    .replace('{{relative}}', imageDate.relative)
                    .replace('{{date}}', imageDate.date)}
                </li>
                <li>
                  {t('skinMoles.imageCarousel.positionInfo')
                    .replace('{{current}}', currentImageIndex + 1)
                    .replace('{{total}}', sortedImages.length)}
                </li>
                {isReferenceImage && (
                  <li className='text-warning'>
                    {t('skinMoles.imageCarousel.referenceImageWarning')}
                  </li>
                )}
              </ul>
            </div>
            {sortedImages.length === 1 && (
              <div className='alert alert-warning mt-2 mb-0'>
                <small>
                  <strong>{t('skinMoles.errors.lastImageWarning')}</strong>
                </small>
              </div>
            )}
          </>
        }
        confirmText={
          sortedImages.length === 1
            ? t('skinMoles.imageCarousel.deleteImageAndMole')
            : t('skinMoles.imageCarousel.deleteImageConfirm')
        }
        variant='danger'
        isLoading={isDeletingImage}
      />

      {/* AI Classification Consent Modal */}
      <AiClassificationConsentModal
        show={showConsentModal}
        onHide={() => setShowConsentModal(false)}
        currentImage={currentImage}
        onConsentGiven={handleConsentGiven}
        isLoading={isConsenting}
      />

      {/* Credits Required Modal */}
      <CreditsRequiredModal
        show={showCreditsRequiredModal}
        onHide={() => setShowCreditsRequiredModal(false)}
        feature='skin_classification'
        creditsNeeded={1}
      />
    </Modal>
  );
};

export default MoleImageCarouselModal;
